import React, { useEffect, useState } from "react";
import axios from "axios";
import { useGuardStore } from "../context/GuardContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function GuardDutyHistory() {
  const { selectedGuard } = useGuardStore();

  const [allData, setAllData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedGuard?.id) {
      fetchDutyHistory(selectedGuard.id);
      return;
    }

    const storedGuard = localStorage.getItem("selectedGuard");
    if (storedGuard) {
      const guardObj = JSON.parse(storedGuard);
      if (guardObj?.id) {
        fetchDutyHistory(guardObj.id);
      }
    }
  }, []);

  const fetchDutyHistory = async (guardId) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("guardToken");

      const res = await axios.get(
        `${BASE_URL}/api/assignments/guard/${guardId}/history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAllData(res.data || []);
    } catch (err) {
      console.log("Duty History Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = allData.filter((item) =>
    `
    ${item.id}
    ${item.category?.name}
    ${item.category?.designation}
    ${item.officer?.name}
    ${item.officer?.rank}
    ${item.status}
    `
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + rowsPerPage
  );
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  return (
    <>
      <div className="duty-history-wrapper">
        {/* Header */}
        <header className="history-header">
          <h1>
            <i className="fas fa-history"></i> Officer Duty History
          </h1>
          <p>Complete record of all past and current duty assignments</p>
        </header>

        {/* Main Content */}
        <main className="history-main">
          {/* Search Bar */}
          <div className="search-card">
            <div className="search-container">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search by VIP name, designation, status, ID..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Table Card */}
          <div className="table-card">
            {loading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading duty history...</p>
              </div>
            ) : paginatedData.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-folder-open"></i>
                <h3>No Duty Records Found</h3>
                <p>{search ? "Try adjusting your search terms" : "No assignments recorded yet"}</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Assignment ID</th>
                        <th>VIP Name</th>
                        <th>Designation</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((item, i) => (
                        <tr key={i}>
                          <td className="id-cell">#{item.assignmentId || item.id || "N/A"}</td>
                          <td className="name-cell">
                            <strong>{item.vipname || item.category?.name || "N/A"}</strong>
                          </td>
                          <td>{item.designation || item.category?.designation || "N/A"}</td>
                          <td>{formatDate(item.startTime)}</td>
                          <td>{formatDate(item.endTime)}</td>
                          <td>
                            <span
                              className={`status-badge ${
                                item.status === "Active"
                                  ? "active"
                                  : item.status === "Inactive" || item.status === "Completed"
                                  ? "inactive"
                                  : "pending"
                              }`}
                            >
                              {item.status || "Unknown"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="nav-btn"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    <div className="page-numbers">
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i + 1)}
                          className={currentPage === i + 1 ? "active" : ""}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="nav-btn"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <style jsx>{`
        .duty-history-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0e7ff 0%, #f8fbff 100%);
          color: #1e293b;
          padding: 2rem;
        }

        .history-header {
          text-align: center;
          margin-bottom: 2.5rem;
          padding: 3rem 2rem;
          background: linear-gradient(135deg, #1e40af, #3b82f6);
          border-radius: 24px;
          color: white;
          box-shadow: 0 15px 40px rgba(30, 64, 175, 0.3);
        }

        .history-header h1 {
          font-size: 2.8rem;
          font-weight: 800;
          margin: 0 0 1rem 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          color:#fff;
        }

        .history-header p {
          font-size: 1.3rem;
          opacity: 0.95;
          margin: 0;
        }

        .history-main {
          max-width: 1400px;
          margin: 0 auto;
        }

        .search-card {
          background: rgba(255, 255, 255, 0.94);
          backdrop-filter: blur(16px);
          border-radius: 20px;
          padding: 1.8rem;
          margin-bottom: 2rem;
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.15);
          border: 1px solid rgba(59, 130, 246, 0.4);
        }

        .search-container {
          position: relative;
          max-width: 600px;
          margin: 0 auto;
        }

        .search-container i {
          position: absolute;
          left: 1.5rem;
          top: 50%;
          transform: translateY(-50%);
          color: #3b82f6;
          font-size: 1.3rem;
        }

        .search-container input {
          width: 100%;
          padding: 1.2rem 1.5rem 1.2rem 4rem;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          font-size: 1.1rem;
          background: #f8fafc;
          transition: all 0.3s ease;
        }

        .search-container input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
          background: white;
        }

        .table-card {
          background: rgba(255, 255, 255, 0.94);
          backdrop-filter: blur(16px);
          border-radius: 24px;
          padding: 2rem;
          box-shadow: 0 15px 40px rgba(59, 130, 246, 0.18);
          border: 1px solid rgba(59, 130, 246, 0.4);
          overflow: hidden;
        }

        .table-responsive {
          overflow-x: auto;
          border-radius: 16px;
        }

        .history-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 1rem;
        }

        .history-table th {
          background: #f1f5f9;
          color: #1e40af;
          font-weight: 700;
          padding: 1.4rem 1.2rem;
          text-align: left;
          border-bottom: 3px solid #3b82f6;
        }

        .history-table tr {
          transition: all 0.3s ease;
        }

        .history-table tr:hover {
          background: rgba(59, 130, 246, 0.08);
          transform: scale(1.01);
        }

        .history-table td {
          padding: 1.4rem 1.2rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .id-cell {
          font-family: monospace;
          font-weight: 600;
          color: #1e40af;
        }

        .name-cell strong {
          color: #1e3a8a;
          font-size: 1.1rem;
        }

        .status-badge {
          padding: 0.6rem 1.2rem;
          border-radius: 30px;
          font-weight: 700;
          font-size: 0.95rem;
          text-transform: capitalize;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.inactive {
          background: #fce4e4;
          color: #c62828;
        }

        .status-badge.pending {
          background: #fff3cd;
          color: #856404;
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 5rem 2rem;
          color: #64748b;
        }

        .loading-state i,
        .empty-state i {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          color: #3b82f6;
        }

        .empty-state h3 {
          font-size: 1.8rem;
          color: #1e40af;
          margin: 1rem 0;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 2.5rem;
          flex-wrap: wrap;
        }

        .nav-btn {
          padding: 0.8rem 1.2rem;
          background: #e2e8f0;
          color: #475569;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1.1rem;
        }

        .nav-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
        }

        .nav-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-numbers {
          display: flex;
          gap: 0.6rem;
        }

        .page-numbers button {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          background: white;
          color: #475569;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-numbers button.active {
          background: #1e40af;
          color: white;
          border-color: #1e40af;
          transform: scale(1.1);
        }

        .page-numbers button:hover:not(.active) {
          border-color: #3b82f6;
          background: #f0f7ff;
        }

        /* Responsive */
        @media (max-width: 992px) {
          .history-header h1 {
            font-size: 2.4rem;
          }
          .history-table th,
          .history-table td {
            padding: 1rem;
          }
        }

        @media (max-width: 768px) {
          .duty-history-wrapper {
            padding: 1rem;
          }

          .history-header {
            padding: 2rem 1.5rem;
          }

          .history-header h1 {
            font-size: 2.1rem;
            flex-direction: column;
          }

          .search-card {
            padding: 1.5rem;
          }

          .table-card {
            padding: 1.5rem;
          }

          .pagination {
            gap: 0.8rem;
          }

          .page-numbers button {
            width: 40px;
            height: 40px;
            font-size: 0.95rem;
          }
        }

        @media (max-width: 480px) {
          .history-header h1 {
            font-size: 1.9rem;
          }

          .search-container input {
            padding: 1rem 1rem 1rem 3.5rem;
            font-size: 1rem;
          }

          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
}

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { useGuardStore } from "../context/GuardContext";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function GuardDutyHistory() {
//   const { selectedGuard } = useGuardStore();

//   const [allData, setAllData] = useState([]);
//   const [search, setSearch] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const rowsPerPage = 20;
//   const [loading, setLoading] = useState(true);

//   //  ALWAYS CALL API ON PAGE LOAD
//   useEffect(() => {
//     if (selectedGuard?.id) {
//       fetchDutyHistory(selectedGuard.id);
//       return;
//     }

//     const storedGuard = localStorage.getItem("selectedGuard");
//     if (storedGuard) {
//       const guardObj = JSON.parse(storedGuard);
//       if (guardObj?.id) {
//         fetchDutyHistory(guardObj.id);
//       }
//     }
//   }, []); // <-- eslint warns about this empty dependency

//   //  API CALL WITH TOKEN
//   const fetchDutyHistory = async (guardId) => {
//     try {
//       setLoading(true);

//       const token = localStorage.getItem("guardToken");

//       const res = await axios.get(
//         `${BASE_URL}/api/assignments/guard/${guardId}/history`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       setAllData(res.data || []);
//     } catch (err) {
//       console.log("Duty History Error:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   //  SEARCH FILTER (HANDLES NESTED FIELDS)
//   const filteredData = allData.filter((item) =>
//     `
//     ${item.id}
//     ${item.category?.name}
//     ${item.category?.designation}
//     ${item.officer?.name}
//     ${item.officer?.rank}
//     ${item.status}
//     `
//       .toLowerCase()
//       .includes(search.toLowerCase())
//   );

//   //  PAGINATION
//   const startIndex = (currentPage - 1) * rowsPerPage;
//   const paginatedData = filteredData.slice(
//     startIndex,
//     startIndex + rowsPerPage
//   );
//   const totalPages = Math.ceil(filteredData.length / rowsPerPage);

//   //  DATE FORMATTER
//   const formatDate = (date) => {
//     if (!date) return "N/A";
//     return new Date(date).toLocaleString();
//   };

//   return (
//     <div style={styles.container}>
//       <div style={styles.title}>Officer Duty History</div>

//       {/* 🔍 SEARCH */}
//       <div style={styles.filterRow}>
//         <div style={styles.filterCard}>
//           <div style={styles.filterLabel}>Search Duty</div>
//           <input
//             style={styles.select}
//             placeholder="Search by name, rank, status..."
//             value={search}
//             onChange={(e) => {
//               setSearch(e.target.value);
//               setCurrentPage(1);
//             }}
//           />
//         </div>
//       </div>

//       {/* 📋 TABLE */}
//       <div style={styles.card}>
//         {loading ? (
//           <div style={styles.noData}>Loading duty history...</div>
//         ) : paginatedData.length === 0 ? (
//           <div style={styles.noData}>No duty history found</div>
//         ) : (
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.th}>Assignment ID</th>
//                 <th style={styles.th}>Vip Name</th>
//                 <th style={styles.th}>Designation</th>
//                 <th style={styles.th}>Start Time</th>
//                 <th style={styles.th}>End Time</th>
//                 <th style={styles.th}>Status</th>
//               </tr>
//             </thead>

//             <tbody>
//               {paginatedData.map((item, i) => (
//                 <tr key={i} style={styles.row}>
//                   <td style={styles.td}>{item.assignmentId}</td>
//                   <td style={styles.td}>
//                     {item.vipname || "N/A"}
//                   </td>
//                   <td style={styles.td}>
//                     {item.designation || "N/A"}
//                   </td>

//                   <td style={styles.td}>{formatDate(item.startTime)}</td>
//                   <td style={styles.td}>{formatDate(item.endTime)}</td>
//                   <td style={styles.td}>
//                     <span
//                       style={
//                         item.status === "Active"
//                           ? styles.statusActive
//                           : item.status === "Inactive"
//                           ? styles.statusInactive
//                           : styles.statusOther
//                       }
//                     >
//                       {item.status}
//                     </span>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         )}

//         {/*  PAGINATION */}
//         {totalPages > 1 && (
//           <div style={styles.paginationContainer}>
//             {[...Array(totalPages)].map((_, i) => (
//               <button
//                 key={i}
//                 style={{
//                   ...styles.pageBtn,
//                   ...(currentPage === i + 1 ? styles.activePage : {}),
//                 }}
//                 onClick={() => setCurrentPage(i + 1)}
//               >
//                 {i + 1}
//               </button>
//             ))}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// /* ------------------ UI STYLES ------------------ */

// const styles = {
//   container: { padding: 30, background: "#fff" },
//   title: { fontSize: 30, fontWeight: 800, marginBottom: 25, color: "#1967d2" },

//   filterRow: { display: "flex", gap: 20, marginBottom: 25 },
//   filterCard: {
//     flex: 1,
//     padding: 18,
//     borderRadius: 12,
//     background: "#fff",
//     boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
//   },
//   filterLabel: { fontSize: 15, fontWeight: 600, marginBottom: 6 },
//   select: {
//     width: "100%",
//     padding: "12px",
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//   },

//   card: {
//     background: "#fff",
//     padding: 10,
//     borderRadius: 15,
//     boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
//   },
//   table: { width: "100%", borderCollapse: "collapse" },
//   th: {
//     background: "#f4f4f6",
//     padding: "16px 10px",
//     textAlign: "left",
//     borderBottom: "2px solid #ddd",
//     fontWeight: 700,
//   },
//   row: { height: 62, transition: "0.25s", cursor: "pointer" },
//   td: { padding: "14px 10px", borderBottom: "1px solid #eee" },

//   statusActive: {
//     background: "#e6ffe6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#2e7d32",
//     fontWeight: 600,
//   },
//   statusInactive: {
//     background: "#ffe6e6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#d32f2f",
//     fontWeight: 600,
//   },
//   statusOther: {
//     background: "#fff8e1",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#ff9800",
//     fontWeight: 600,
//   },

//   noData: {
//     textAlign: "center",
//     padding: 40,
//     fontSize: 18,
//     color: "#777",
//   },

//   paginationContainer: {
//     display: "flex",
//     justifyContent: "center",
//     gap: 10,
//     padding: 15,
//   },
//   pageBtn: {
//     padding: "6px 14px",
//     borderRadius: 6,
//     border: "1px solid #1967d2",
//     background: "white",
//     cursor: "pointer",
//   },
//   activePage: {
//     background: "#1967d2",
//     color: "#fff",
//     fontWeight: 700,
//   },
// };
